import { Big } from 'big.js';
import { z } from 'zod/v4';
import { memoize } from '@interxfi/utilities/cache/memoize';
import { getCurrencyFormatter } from '@interxfi/utilities/currency/formatting';
import { getIntlNumberFormat } from '@interxfi/utilities/intl/number';
import { parseNumber } from '@interxfi/utilities/number/parse';

interface NumericStringValidatorOptions<TRequired extends boolean | '' | string[] = false> {
  maxDecimalPlaces?: number;
  eq?: number;
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  required?: TRequired;
  displayFormatter?: (value: string | number | bigint) => string;
  eqError?: string;
  gtError?: string;
  gteError?: string;
  ltError?: string;
  lteError?: string;
}

function getDefaultFormatter(maxDecimalPlaces?: number) {
  const i18nFormatter = getIntlNumberFormat('en-US', { maximumFractionDigits: maxDecimalPlaces });

  return i18nFormatter.format;
}

export function getPercentageValidationStringFormatter(maxDecimalPlaces?: number) {
  const i18nFormatter = getIntlNumberFormat('en-US', { style: 'percent', maximumFractionDigits: maxDecimalPlaces });

  return (str: string | number | bigint) => {
    const asBig = safelyGetBig(str.toString());
    if (!asBig) {
      return '';
    }

    return i18nFormatter.format(asBig.div(100).toNumber());
  };
}

export function getCurrencyValidationStringFormatter(currency = 'USD', maxDecimalPlaces?: number) {
  const formatter = getCurrencyFormatter('en-US', { currency, maximumFractionDigits: maxDecimalPlaces });

  return (str: string | number | bigint) => {
    const asBig = safelyGetBig(str.toString());
    if (!asBig) {
      return '';
    }

    return formatter.format(asBig.toNumber());
  };
}

export const DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER = getCurrencyValidationStringFormatter('USD', 2);
export const DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER_WITHOUT_FRACTIONALS = getCurrencyValidationStringFormatter('USD', 0);
export const DEFAULT_INTEGER_VALIDATION_DISPLAY_FORMATTER = getDefaultFormatter(0);

function safelyGetBig(value: string) {
  try {
    const parsedValue = parseNumber(value);
    return new Big(parsedValue);
  } catch {
    return undefined;
  }
}

function bigRefine(method: 'gt' | 'gte' | 'lt' | 'lte' | 'eq', numericString: string | undefined, comparisonValue: number) {
  if (!numericString) {
    return true;
  }

  const numericValue = safelyGetBig(numericString);

  // Required checking done by the first refine call, with valid number checking by the second refine call
  if (!numericValue) {
    return true;
  }

  return numericValue[method](comparisonValue);
}

export function get24HourTimeWithSecondsStringValidator(fieldLabel: string, required?: boolean) {
  return z
    .string()
    .refine(
      (value) => {
        if (value === undefined || value.trim() === '') {
          return !required;
        }

        return true;
      },
      { message: `${fieldLabel} must be provided` },
    )
    .refine(
      (value) => {
        const [hh, mm, ss] = (value || '').split(':').map((x) => x.trim());
        if (hh?.length !== 2 || mm?.length !== 2 || ss?.length !== 2) {
          return false;
        }

        return [Number(hh), Number(mm), Number(ss)].every((x) => !Number.isNaN(x));
      },
      {
        message: `${fieldLabel} must be in HH:MM:SS format`,
      },
    )
    .refine(
      (value) => {
        const [hh, mm, ss] = (value || '').split(':').map((x) => x.trim());
        if (hh?.length !== 2 || mm?.length !== 2 || ss?.length !== 2) {
          return false;
        }

        const [hhNum, mmNum, ssNum] = [Number(hh), Number(mm), Number(ss)];

        return hhNum >= 0 && hhNum < 24 && mmNum >= 0 && mmNum < 60 && ssNum >= 0 && ssNum < 60;
      },
      {
        message: `${fieldLabel} must be a valid time between 00:00:00 and 23:59:59`,
      },
    );
}

export function get24HourTimeStringValidator(fieldLabel: string, required?: boolean) {
  return z
    .string()
    .refine(
      (value) => {
        if (value === undefined || value.trim() === '') {
          return !required;
        }

        return true;
      },
      { message: `${fieldLabel} must be provided` },
    )
    .refine(
      (value) => {
        const [hh, mm] = (value || '').split(':').map((x) => x.trim());
        if (hh?.length !== 2 || mm?.length !== 2) {
          return false;
        }

        return [Number(hh), Number(mm)].every((x) => !Number.isNaN(x));
      },
      {
        message: `${fieldLabel} must be in HH:MM format`,
      },
    )
    .refine(
      (value) => {
        const [hh, mm] = (value || '').split(':').map((x) => x.trim());
        if (hh?.length !== 2 || mm?.length !== 2) {
          return false;
        }

        const [hhNum, mmNum] = [Number(hh), Number(mm)];

        return hhNum >= 0 && hhNum < 24 && mmNum >= 0 && mmNum < 60;
      },
      {
        message: `${fieldLabel} must be a valid time between 00:00 and 23:59`,
      },
    );
}

export function get24HourTimeRangeRefineValidator<T extends Record<string, unknown>>(isRequired: boolean, startName: keyof T, endName: keyof T) {
  return function validate(data: T) {
    if (!data) {
      return !isRequired;
    }

    const startValue = startName in data && data[startName] && typeof data[startName] === 'string' ? data[startName] : '';
    const endValue = endName in data && data[endName] && typeof data[endName] === 'string' ? data[endName] : '';

    const [openHr, openMin] = startValue.split(':').map(Number);
    const openVal = openHr * 60 + openMin;
    const [closeHr, closeMin] = endValue.split(':').map(Number);
    const closeVal = closeHr * 60 + closeMin;

    return openVal <= closeVal;
  };
}

export const ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE = ['', '$'] as const satisfies string[];

export const getNumericStringValidator = memoize(function getNumericStringValidator<TRequired extends boolean | '' | string[] = false>(
  fieldLabel: string,
  options: NumericStringValidatorOptions<TRequired>,
): TRequired extends false ? z.ZodOptional<z.ZodString> : z.ZodString {
  const { eq, gt, gte, lt, lte, maxDecimalPlaces, required, displayFormatter = getDefaultFormatter(maxDecimalPlaces) } = options;
  const emptyValue = options.displayFormatter?.('');

  let base: z.ZodString | z.ZodOptional<z.ZodString> | z.ZodUnion<[z.ZodString, z.ZodLiteral<''>]> = z
    .string()
    .refine(
      (value) => {
        if (value === undefined || value.trim() === '' || value === emptyValue) {
          if (Array.isArray(required)) {
            return required.includes(value);
          }

          return !required;
        }

        return true;
      },
      { message: `${fieldLabel} must be provided` },
    )
    .refine(
      (value) => {
        if (value === undefined || value.trim() === '' || value === emptyValue || (Array.isArray(required) && required.includes(value))) {
          return true;
        }

        return Boolean(safelyGetBig(value));
      },
      { message: `${fieldLabel} must be a valid number` },
    )
    .refine(
      (value) => {
        if (maxDecimalPlaces === undefined) {
          return true;
        }

        const splitValue = (value as string | undefined)?.split('.') || [];
        if (!splitValue.length) {
          return true;
        }

        return splitValue.length === 1 || splitValue[1].length <= maxDecimalPlaces;
      },
      {
        message: !maxDecimalPlaces
          ? `${fieldLabel} must be an integer`
          : `${fieldLabel} must have at most ${displayFormatter(maxDecimalPlaces)} decimal places`,
      },
    )
    .refine((value) => eq === undefined || bigRefine('eq', value, eq), {
      message: options?.eqError ?? `${fieldLabel} must be equal to ${displayFormatter(eq ?? 0)}`,
    })
    .refine((value) => gt === undefined || bigRefine('gt', value, gt), {
      message: options.gtError ?? `${fieldLabel} must be greater than ${displayFormatter(gt ?? 0)}`,
    })
    .refine((value) => gte === undefined || bigRefine('gte', value, gte), {
      message: options.gteError ?? `${fieldLabel} must be greater than or equal to ${displayFormatter(gte ?? 0)}`,
    })
    .refine((value) => lt === undefined || bigRefine('lt', value, lt), {
      message: options.ltError ?? `${fieldLabel} must be less than ${displayFormatter(lt ?? 0)}`,
    })
    .refine((value) => lte === undefined || bigRefine('lte', value, lte), {
      message: options.lteError ?? `${fieldLabel} must be less than or equal to ${displayFormatter(lte ?? 0)}`,
    });

  if (!required) {
    base = required === '' ? base.or(z.literal('')) : base.optional();
  }

  return base as TRequired extends false ? z.ZodOptional<z.ZodString> : z.ZodString;
});
